import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/sharp-solid-svg-icons';
import { usePathname, useRouter } from 'next/navigation';
import { type FC, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import { InsightsIcon } from '@/components/icons/InsightsIcon';
import Typography from '@/components/typography';
import { INSIGHT_URL, PLAT_MAP_URL_LANDING } from '@/constants/urls';
import { SelectionsPanelContainer } from '@/features/selectionsPanel/selectionsPanelContainer';
import { SelectionsPanelEmptyContent } from '@/features/selectionsPanel/selectionsPanelEmptyContent';
import { SelectionsPanelFooter } from '@/features/selectionsPanel/selectionsPanelFooter';
import { SelectionsPanelHeader } from '@/features/selectionsPanel/selectionsPanelHeader';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useAuth } from '@/providers/auth-provider';
import { useModalContext } from '@/providers/ModalProvider';

import { BoundariesSectionDropdown } from './sectionDropdown/boundariesSectionDropdown';
import { DeedSectionDropdown } from './sectionDropdown/deedSectionDropdown';
import { DrawSectionDropdown } from './sectionDropdown/drawLandSectionDropdown';
import { ListingsSectionDropdown } from './sectionDropdown/listingsSectionDropdown';
import { SoldLandSectionDropdown } from './sectionDropdown/soldLandSectionDropdown';

const headerHeightInPx = 153;
const footerHeightInPx = 56;

export const SelectionsCardsPanel: FC = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { isAnonymous } = useAuth();
  const { openAuthModal } = useModalContext();

  const { isPanelOpen, setIsExportMenuOpen, totalSelectionCount } =
    useSelectionsPanelContext();

  const handleInsightsClick = useCallback(() => {
    if (pathname?.includes(INSIGHT_URL)) {
      router.push(PLAT_MAP_URL_LANDING);
      return;
    }

    router.push(INSIGHT_URL);
  }, [pathname, router]);

  const handleExportClick = useCallback(() => {
    if (isAnonymous) {
      openAuthModal('login');
      return;
    }
    setIsExportMenuOpen(true);
  }, [isAnonymous, openAuthModal, setIsExportMenuOpen]);

  return (
    <SelectionsPanelContainer>
      <SelectionsPanelHeader
        headerHeightInPx={isPanelOpen ? headerHeightInPx : undefined}
      >
        <IconButton
          className={twMerge(
            'flex h-8 w-full flex-row items-center justify-center gap-3 bg-blue-utility px-3 py-2 hover:bg-blue-70'
          )}
          size={ButtonSize.MEDIUM}
          variant={ButtonVariant.PRIMARY}
          onClick={handleExportClick}
        >
          <FontAwesomeIcon icon={faShare} />
          <Typography variant="button2">Export</Typography>
        </IconButton>
        <IconButton
          className={twMerge(
            'flex h-8 w-full flex-row items-center justify-center gap-3 px-3 py-2'
          )}
          size={ButtonSize.MEDIUM}
          variant={ButtonVariant.SECONDARY}
          onClick={handleInsightsClick}
        >
          <InsightsIcon className="w-[22px]" color="currentColor" />
          <Typography variant="button2">Insights</Typography>
        </IconButton>
      </SelectionsPanelHeader>
      {isPanelOpen && (
        <>
          <div
            className="overflow-y-scroll bg-gray-20 scrollbar-hide"
            style={{
              height: `calc(100vh - ${headerHeightInPx}px - ${footerHeightInPx}px)`,
            }}
          >
            {!totalSelectionCount ? (
              <SelectionsPanelEmptyContent />
            ) : (
              <>
                <BoundariesSectionDropdown />
                <SoldLandSectionDropdown />
                <ListingsSectionDropdown />
                <DrawSectionDropdown />
                <DeedSectionDropdown />
              </>
            )}
          </div>
          <SelectionsPanelFooter footerHeightInPx={footerHeightInPx} />
        </>
      )}
    </SelectionsPanelContainer>
  );
};
